import * as Sentry from '@sentry/react'
import env from 'env'

// jsonapi-react never rejects on the fetch itself: it wraps fetch in its own
// .catch(), so a failed mutation resolves as { errors } (JSON:API body) or
// { error } (network / CORS / non-JSON response). Neither reaches the Sentry
// ErrorBoundary, which is why these failures were invisible. See RPD-17256.
function classify(res) {
  if (res?.errors) {
    return 'validation_error'
  }
  if (res?.error?.name && !res.error.detail) {
    return 'request_rejected'
  }
  return 'api_error'
}

// GraphitiPlugin's formatErrors replaces the JSON:API array with
// { attribute: [{ status, code, title, detail, message }] } (no source pointer),
// so read that shape first — it is what the app actually receives.
function firstError(res) {
  if (Array.isArray(res?.errors)) {
    return res.errors[0] || {}
  }

  if (res?.errors) {
    const [attribute] = Object.keys(res.errors)
    const value = res.errors[attribute]

    return {
      ...(Array.isArray(value) ? value[0] : {}),
      attribute,
    }
  }

  return res?.error || {}
}

/**
 * Report a failed jsonapi-react mutation to Sentry and PostHog.
 *
 * `context` must carry diagnostic values only (city, state, service_type,
 * presence booleans) — never vendor PII. The row is persisted server-side on
 * success, so telemetry does not need to reconstruct the submission.
 */
export default function reportSubmissionFailure({ event, res, posthog, context = {} }) {
  const first = firstError(res)

  const payload = {
    failure_type: classify(res),
    error_status: first.status || null,
    error_code: first.code || null,
    error_detail: first.detail || first.message || first.title || null,
    error_attribute: first.attribute || null,
    ...context,
  }

  // No DSN gate: captureMessage is a no-op when init never ran (client.js:36).
  Sentry.captureMessage(event, { level: 'error', extra: payload })

  // Sentry is not initialized locally, so keep the failure visible
  if (env.APP_ENV === 'development') {
    console.error(event, payload)
  }

  posthog?.capture(event, payload)

  return payload
}
